import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Typography, Box, ButtonBase, Stack } from '@mui/material';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

import DottedTimer from './DottedTimer';
import useTimerState from './useTimerState';
import SuccessAnimation from './SuccessAnimation';

type TimerProps = {
  minutes: number;
  seconds?: number;
  isRunning: boolean;
  onTimerEnd?: () => void;
  displayMode?: 'default' | 'quarters' | 'minutes';
  timerId?: number;
  color?: string;
  completed?: boolean;
  onClick?: () => void;
};

const Timer: React.FC<TimerProps> = ({
  minutes,
  seconds = 0,
  isRunning,
  onTimerEnd,
  displayMode = 'default',
  timerId,
  color = 'blue',
  completed = false,
  onClick,
}) => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const { time, timerDone } = useTimerState({ minutes, seconds, isRunning, onTimerEnd, timerId });

  const containerRef = useRef<HTMLButtonElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const clockView = localStorage.getItem('timerClock')?.toLowerCase() === 'true';
  const totalInitialSeconds = minutes * 60 + seconds;
  const showQuarters = displayMode === 'quarters';

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const responsiveFontSize = useMemo(() => {
    const baseFontSize = 16;
    const scaleFactor = 0.15;
    return Math.max(baseFontSize, containerWidth * scaleFactor);
  }, [containerWidth]);

  const timerPercentage = useMemo(() => {
    if (clockView) {
      const maxMinutes = 60;
      const maxSeconds = maxMinutes * 60;
      return Math.max(0, Math.min(100, (time / maxSeconds) * 100));
    }

    return (time / totalInitialSeconds) * 100;
  }, [time, totalInitialSeconds, clockView, displayMode]);

  const formatTime = (timeInSeconds: number, format?: 'nosec') => {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = timeInSeconds % 60;

    if (displayMode === 'minutes') {
      if (hours > 0 && minutes > 0) {
        return `${hours}h \n ${minutes}${t('min')}`;
      }
      if (hours > 0 && minutes === 0) {
        return `${hours}h`;
      }
      return `${minutes}${t('min')}`;
    }

    if (minutes > 0 && format === 'nosec') {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    if (format === 'nosec') {
      return `${seconds}`;
    }

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
    }

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const quarters = useMemo(() => {
    if (!clockView) {
      const val2 = Math.round((totalInitialSeconds * 0.25) / 60);
      const val4 = Math.round((totalInitialSeconds * 0.75) / 60);

      const left2 = val2 > 60 ? '105%' : '100%';
      const left4 = val4 > 60 ? '-5%' : '0%';

      return [
        { value: 0, left: '50%', top: 0, transform: 'translate(-50%, 0)' },
        {
          value: formatTime(val2, 'nosec'),
          left: left2,
          top: '50%',
          transform: 'translate(-100%, -50%)',
        },
        {
          value: formatTime(Math.round((totalInitialSeconds * 0.5) / 60), 'nosec'),
          left: '50%',
          top: '100%',
          transform: 'translate(-50%, -100%)',
        },
        {
          value: formatTime(val4, 'nosec'),
          left: left4,
          top: '50%',
          transform: 'translate(0, -50%)',
        },
      ];
    } else {
      return [
        { value: 0, left: '50%', top: '0', transform: 'translate(-50%, 0)' },
        { value: 15, left: '100%', top: '50%', transform: 'translate(-100%, -50%)' },
        { value: 30, left: '50%', top: '100%', transform: 'translate(-50%, -100%)' },
        { value: 45, left: '0', top: '50%', transform: 'translate(0, -50%)' },
      ];
    }
  }, [displayMode, totalInitialSeconds]);

  const animationStyles = {
    hidden: {
      transform: 'scale(4) translate(0, -25%)',
      opacity: 0,
      transition: 'all 0.35s ease-out',
    },
    visible: {
      opacity: 1,
      transition: 'opacity 0.1s linear',
      transitionDelay: '0.1s',
    },
  };

  const styles = {
    wrapper: {
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: showQuarters ? '1.25em' : '0',
      borderRadius: '50%',
    },
    timerText: {
      fontSize: responsiveFontSize,
      position: 'absolute',
    },
    pulseText: {
      animation: 'pulseText 6s ease-in-out infinite alternate',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 1 },
    },
    statusStack: {
      position: 'absolute',
      top: `calc(50% + ${responsiveFontSize / 2}px)`,
      justifyContent: 'start',
      alignItems: 'center',
      direction: 'column',
    },
    iconContainer: {
      position: 'relative',
      height: '1.25em',
      width: '5em',
    },
    quartersContainer: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      background: 'transparent',
      zIndex: 10,
    },
    quarterText: (quarter: any) => ({
      position: 'absolute',
      left: quarter.left,
      top: quarter.top,
      opacity: 1,
      transform: quarter.transform,
      whiteSpace: 'nowrap',
      textAlign: 'center',
      overflow: 'visible',
    }),
  };

  return (
    <ButtonBase
      sx={styles.wrapper}
      role={onClick ? 'button' : undefined}
      onClick={onClick}
      ref={containerRef}
      disabled={!onClick || completed || timerDone}
    >
      <style>{`
        @keyframes pulseText {
          0% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      <DottedTimer
        percentage={timerPercentage}
        color={color}
        stroke={palette.black}
        backgroundColor={palette.background.default}
        text
        style={{ width: '100%' }}
      />

      {completed ? (
        <Box sx={{ position: 'absolute' }}>
          <SuccessAnimation />
        </Box>
      ) : (
        <>
          <Typography variant="h2" sx={styles.timerText}>
            {formatTime(time)}
          </Typography>

          {onClick && (
            <Stack sx={styles.statusStack}>
              {timerDone ? (
                <Typography variant="caption" color="text.secondary" sx={styles.pulseText}>
                  {t('No time left')}
                </Typography>
              ) : (
                <>
                  <Box sx={styles.iconContainer}>
                    <PauseIcon
                      sx={{
                        color: 'grey.700',
                        position: 'absolute',
                        margin: 'auto',
                        left: 0,
                        right: 0,
                        top: 0,
                        ...(isRunning ? animationStyles.visible : animationStyles.hidden),
                      }}
                    />
                    <PlayArrowIcon
                      sx={{
                        color: 'grey.700',
                        position: 'absolute',
                        margin: 'auto',
                        left: 0,
                        right: 0,
                        top: 0,
                        ...(isRunning ? animationStyles.hidden : animationStyles.visible),
                      }}
                    />
                  </Box>

                  {!isRunning && (
                    <Typography variant="caption" color="text.secondary" sx={styles.pulseText}>
                      {t('Paused')}
                    </Typography>
                  )}
                </>
              )}
            </Stack>
          )}
        </>
      )}

      {showQuarters && (
        <Box sx={styles.quartersContainer}>
          {quarters.map((quarter, index) => (
            <Typography key={index} sx={styles.quarterText(quarter)}>
              {quarter.value}
            </Typography>
          ))}
        </Box>
      )}
    </ButtonBase>
  );
};

export default Timer;
